import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { get, isNil, setWith } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import type {
  IProductVariationSize,
  ISizesConfig,
  ISizesStyle,
} from 'src/interfaces';
import { lang } from 'src/utils';

import { CategoryConfigAccordion } from '../categoryConfigAccordion/CategoryConfigAccordion';
import { CategoryConfigItem } from '../categoryConfigItem/CategoryConfigItem';
import { CategoryConfigAccordionListProps } from './CategoryConfigAccordionList.interface';

const EMPTY_SIZE_CONFIG = Object.freeze({}) as ISizesConfig;
const EMPTY_SIZE_ERRORS = Object.freeze({}) as Record<string, never>;
const PER_SIZE_OPTIONS = Object.freeze({ hideDisplayLabel: true });
const APPLY_ALL_OPTIONS = Object.freeze({
  hideDisplayLabel: true,
  showLabelBgColor: true,
  showLabelHeight: true,
  showFlavors: true,
  showDisclaimer: true,
});
const APPLY_ALL_ALACARTE_OPTIONS = Object.freeze({ hideDisplayLabel: true });

export const CategoryConfigAccordionList = (
  props: CategoryConfigAccordionListProps,
) => {
  const propsRef = useRef(props);
  propsRef.current = props;

  const globalThemeCategoryKey = Object.keys(
    props.theme?.config.category ?? {},
  )[0];

  const createSizeChange = useCallback(
    (field?: IProductVariationSize['name']) => (value: ISizesConfig) => {
      const { value: currentValue, onChange } = propsRef.current;

      if (!isNil(field)) {
        onChange(
          setWith({ ...currentValue } as ISizesStyle, `["${field}"]`, value),
        );
      } else {
        onChange({ defaultConfig: value });
      }
    },
    [],
  );

  const applyAllChangeHandler = useMemo(
    () => createSizeChange(),
    [createSizeChange],
  );

  const applyAllAlacarteChangeHandler = useMemo(
    () => createSizeChange(globalThemeCategoryKey),
    [createSizeChange, globalThemeCategoryKey],
  );

  const sizeChangeHandlers = useMemo(() => {
    const handlers = new Map<string, (value: ISizesConfig) => void>();

    props.variationSizes.forEach((size) => {
      handlers.set(size.name, createSizeChange(size.name));
    });

    return handlers;
  }, [props.variationSizes, createSizeChange]);

  return props.isApplyAll ? (
    <>
      <CategoryConfigItem
        isApplyAll={true}
        onChange={applyAllChangeHandler}
        value={props.value?.defaultConfig}
        theme={props?.theme}
        options={APPLY_ALL_OPTIONS}
      />
      {props.options?.enableIndividualStyles && (
        <Accordion defaultExpanded={false}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            style={{ borderBottom: '1px solid #000' }}>
            <Typography fontWeight={400}>{lang.alacarteStyling}</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <CategoryConfigItem
              value={props.value?.[globalThemeCategoryKey]}
              theme={props?.theme}
              variationSizeName={globalThemeCategoryKey}
              options={APPLY_ALL_ALACARTE_OPTIONS}
              onChange={applyAllAlacarteChangeHandler}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  ) : (
    <>
      {props.variationSizes.map((size) => {
        const sizeDetails = get(props.value, `${size.name}`, EMPTY_SIZE_CONFIG);
        const sizeDetailsErrors = get(
          props.errors?.productList?.config,
          `${size.name}`,
          EMPTY_SIZE_ERRORS,
        );

        return (
          <CategoryConfigAccordion
            key={size.id}
            variationSizeName={size.name}
            value={sizeDetails}
            theme={props?.theme}
            errors={sizeDetailsErrors}
            onChange={
              sizeChangeHandlers.get(size.name) ?? createSizeChange(size.name)
            }
            options={PER_SIZE_OPTIONS}
          />
        );
      })}
    </>
  );
};
